import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Button, Grid, IconButton, Tooltip } from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useAuth } from '../../contexts/AuthContext';
import LogTheme from './LogTheme';

const ServiceLogs = () => {
  const { userId } = useAuth();
  const [serviceLogs, setServiceLogs] = useState([]);
  const [sortOrder, setSortOrder] = useState('newest'); // State-variabel för sorteringsordningen
  const [viewMode, setViewMode] = useState('grid'); // 'grid' eller 'list'

  useEffect(() => {
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/servicelogs/${userId}`)
      .then(response => response.json())
      .then(data => {
        // Sortera loggarna baserat på sorteringsordningen
        const sortedLogs = data.sort((a, b) => {
          if (sortOrder === 'newest') {
            return new Date(b.date) - new Date(a.date);
          } else {
            return new Date(a.date) - new Date(b.date);
          }
        });
        setServiceLogs(sortedLogs);
      })
      .catch(error => console.error('Error:', error));
  }, [userId, sortOrder]); // Lägg till sortOrder som en beroende

  // Funktion för att växla sorteringsordningen
  const toggleSortOrder = () => {
    setSortOrder(prevOrder => (prevOrder === 'newest' ? 'oldest' : 'newest'));
  };

  // Funktion för att växla mellan grid och list vy
  const toggleViewMode = () => {
    setViewMode(prevMode => prevMode === 'grid' ? 'list' : 'grid');
  };

  return (
    <LogTheme>
      <Box padding={5}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h1">Service</Typography>
          <Button component={Link} to="/createservicelog" variant="contained" color="primary">
            Ny service
          </Button>
        </Box>
        <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
          <Tooltip title={viewMode === 'grid' ? 'Visa som lista' : 'Visa som rutnät'}>
            <IconButton 
              onClick={toggleViewMode} 
              color="primary"
              sx={{ border: '1px solid', borderColor: 'primary.main' }}
            >
              {viewMode === 'grid' ? <ViewListIcon /> : <ViewModuleIcon />}
            </IconButton>
          </Tooltip>
          <Button onClick={toggleSortOrder} variant="contained" color="secondary">
            SORTERA {sortOrder === 'newest' ? '↓' : '↑'}
          </Button>
        </Box>
        
        {viewMode === 'grid' ? (
          <Grid container spacing={2}>
            {serviceLogs.map(serviceLog => (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={serviceLog._id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                      {serviceLog.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {new Date(serviceLog.date).toLocaleDateString('sv-SE')}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        mb: 2, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {serviceLog.description}
                    </Typography>
                    <Button 
                      component={Link} 
                      to={`/showservicelog/${serviceLog._id}`} 
                      variant="contained" 
                      color="primary"
                      fullWidth
                    >
                      Visa
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          serviceLogs.map(serviceLog => (
            <Card key={serviceLog._id} sx={{ marginBottom: 2 }}>
              <CardContent>
                <Typography variant="h4">{serviceLog.title}</Typography>
                <Typography variant="h6">{new Date(serviceLog.date).toLocaleDateString('sv-SE')}</Typography>
                <Typography variant="body1">{serviceLog.description}</Typography>
                <Button component={Link} to={`/showservicelog/${serviceLog._id}`} variant="contained" color="primary">
                  Visa
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </Box>
    </LogTheme>
  );
};

export default ServiceLogs;